import React, { useEffect, useState } from 'react';
import BunkerLossEdit from './BunkerLossEdit';
import { FuelRobColumn } from 'src/types/ViewModel/Bunkering/FuelRobColumn';
import { getBunkeringsOnboardAtDate, getFuelTanks } from '../BunkeringUtils';
import { addDateHours } from 'odfjell-date-time-utils/dateTimeUtils';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { useVesselStore } from 'src/context/VesselContext';
import { useBunkerContext } from 'src/context/BunkerContext';
import { BunkerLossReason, getBunkerLossReason } from 'src/types/ViewModel/Bunkering/BunkerLossReason';
import BunkerLossList from './BunkerLossList';
import { useAolStore } from 'src/context/AolContext';
import { getAolTabInfo, getTimelineText } from 'src/components/aol/AolUtils';
import { BunkerLoss } from 'src/types/ViewModel/Bunkering/BunkerLoss';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { mapBunkerLossData } from 'src/factory/Bunkering/BunkeringFactory';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';

interface BunkerLossProps {
    column: FuelRobColumn;
    selectedDate: Date;
    onClose: () => void;
}

interface BunkerLossVM {
    bunkerLossData: BunkerLoss[];
    selectedBunkerLoss?: BunkerLoss | null;
}

const BunkerLossContainer: React.FC<BunkerLossProps> = ({ column, selectedDate, onClose }) => {
    const [bunkerLossVM, setBunkerLossVM] = useState<BunkerLossVM>({ bunkerLossData: [] });
    const [validLossValue, setValidLossValue] = useState<boolean>(false);
    const [validComment, setValidComment] = useState<boolean>(true);
    const [enableSave, setEnableSave] = useState<boolean>(false);
    const [saveOngoing, setSaveOngoing] = useState<boolean>(false);
    const { aolDataItems } = useAolStore();
    const { bunkerings, bunkerLossData } = useBunkerContext();
    const { upsertBunkerLossData } = useGetBunkeringData();
    const { vessel } = useVesselStore();
    const [tankOptions, setTankOptions] = useState<Tank[]>([]);

    const availableBunkerings = getBunkeringsOnboardAtDate(bunkerings, addDateHours(selectedDate, 12));

    useEffect(() => {
        // Instead of calling onClose directly when the upsertBunkerLossData is done, we set a flag to true and then close after bunkerLossData has been updated.
        // This is because otherwise, the modal will close before the state update has happened within the useGetBunkeringData hook
        // (the updated data returned from upsertBunkerLossData would not be written to bunkerLossData yet).
        if (saveOngoing) {
            onClose();
        }
    }, [bunkerLossData]);

    useEffect(() => {
        checkEnableSaveButton();
    }, [
        bunkerLossVM.bunkerLossData,
        bunkerLossVM?.selectedBunkerLoss?.reason,
        bunkerLossVM?.selectedBunkerLoss?.quantityMT,
        bunkerLossVM?.selectedBunkerLoss?.comment,
    ]);

    useEffect(() => {
        const _newBunkerLoss = {
            bunkerLossId: 0,
            bunkerLossGuid: window.crypto.randomUUID(),
            noonReportId: column.noonReportId,
            bunkeringGuid: null,
            tankId: null,
            reason: null,
            quantityMT: 0,
        } as BunkerLoss;
        setBunkerLossVM({ bunkerLossData: bunkerLossData.filter((l) => l.noonReportId === column.noonReportId), selectedBunkerLoss: _newBunkerLoss });
    }, [bunkerLossData]);

    const handleSelectBunkering = (changeObject) => {
        const selectedBunkering = availableBunkerings.find((b) => b.bunkeringReference === changeObject.event.target.value);
        const _tankOptions = column.fuelRobs.filter((rob) => rob.bunkeringGuid === selectedBunkering.bunkeringGuid).map((rob) => rob.tankId);
        const tanks = getFuelTanks(vessel);
        setTankOptions(_tankOptions.map((tankId) => tanks.find((tank) => tank.tankId === tankId)));

        const _vm = { ...bunkerLossVM };
        _vm.selectedBunkerLoss.bunkeringGuid = selectedBunkering.bunkeringGuid;
        _vm.selectedBunkerLoss.tankId = null;
        _vm.selectedBunkerLoss.quantityMT = 0;
        setBunkerLossVM(_vm);
        setEnableSave(false);
    };

    const handleSelectTank = (changeObject) => {
        const tank = tankOptions.find((t) => t.tankName === changeObject.event.target.value);
        const _vm = { ...bunkerLossVM };
        _vm.selectedBunkerLoss.tankId = tank.tankId;
        _vm.selectedBunkerLoss.quantityMT = 0;
        setBunkerLossVM(_vm);
        setEnableSave(false);
    };

    const checkEnableSaveButton = () => {
        const selectedTankRob = column.fuelRobs.find((rob) => rob.tankId === bunkerLossVM?.selectedBunkerLoss?.tankId)?.rob;
        const isValidLossReason = Object.values(BunkerLossReason).includes(bunkerLossVM?.selectedBunkerLoss?.reason);
        const isValidLossQuantity =
            bunkerLossVM?.selectedBunkerLoss?.quantityMT > 0 && bunkerLossVM?.selectedBunkerLoss?.quantityMT <= selectedTankRob;
        setValidLossValue(isValidLossReason && isValidLossQuantity);
        const _validComment =
            bunkerLossVM?.selectedBunkerLoss?.reason !== BunkerLossReason.OtherLossReason || bunkerLossVM?.selectedBunkerLoss?.comment?.length > 0;
        setValidComment(_validComment);

        const deletedLoss = bunkerLossData.filter((l) => l.noonReportId === column.noonReportId).length > bunkerLossVM.bunkerLossData.length;
        const _enableSave =
            (isValidLossReason && isValidLossQuantity && _validComment) || (deletedLoss && !bunkerLossVM?.selectedBunkerLoss?.bunkeringGuid);

        setEnableSave(_enableSave);
    };

    const handleChangeLossReason = (changeObject) => {
        const reason = getBunkerLossReason(changeObject.event.target.value);
        const _vm = { ...bunkerLossVM };
        _vm.selectedBunkerLoss.reason = reason;
        setBunkerLossVM(_vm);
    };

    const handleChangeLoss = (changeObject) => {
        const value = parseFloat(changeObject.event.target.value);
        const _vm = { ...bunkerLossVM };
        _vm.selectedBunkerLoss.quantityMT = value;
        setBunkerLossVM(_vm);
    };

    const handleChangeComment = (changeObject) => {
        const comment = changeObject.event.target.value;
        const _vm = { ...bunkerLossVM };
        _vm.selectedBunkerLoss.comment = comment;
        setBunkerLossVM(_vm);
    };

    const handleConfirm = () => {
        const bunkerLossData = [...bunkerLossVM.bunkerLossData];
        if (validLossValue && validComment && !bunkerLossData.some((l) => l.bunkerLossGuid === bunkerLossVM.selectedBunkerLoss.bunkerLossGuid))
            bunkerLossData.push(bunkerLossVM.selectedBunkerLoss);
        const bms = mapBunkerLossData(bunkerLossData);
        upsertBunkerLossData(bms, vessel.imoNumber, column.noonReportId).then(() => {
            setSaveOngoing(true);
        });
    };

    const handleDeleteBunkerLoss = (bunkerLoss: BunkerLoss, e: React.MouseEvent) => {
        e.stopPropagation();
        const _vm = { ...bunkerLossVM };
        _vm.bunkerLossData = _vm.bunkerLossData.filter((loss) => loss.bunkerLossGuid !== bunkerLoss.bunkerLossGuid);
        setBunkerLossVM(_vm);
    };

    const handleSelectBunkerLoss = (bunkerLoss: BunkerLoss) => {
        const _vm = { ...bunkerLossVM };
        _vm.selectedBunkerLoss = bunkerLoss;
        setBunkerLossVM(_vm);
    };

    return (
        <OdfjellPanel
            headerTitle={
                <>
                    <div>Bunker Loss - {getAolTabInfo(aolDataItems.find((a) => a.noonReport?.metadata?.noonReportId === column.noonReportId))}</div>
                    <div>{getTimelineText(aolDataItems.find((a) => a.noonReport?.metadata?.noonReportId === column.noonReportId))}</div>
                </>
            }
            defaultPosition="center"
            panelWidth={PANEL_CONSTANTS.VERY_LARGE_WIDTH}
            actionButtonText="Save"
            isActionDisabled={!enableSave || !validComment}
            onAction={handleConfirm}
            onCancel={onClose}
            showCancelButton={true}
        >
            {
                <div>
                    {bunkerLossVM.bunkerLossData && bunkerLossVM.bunkerLossData.length > 0 && (
                        <BunkerLossList
                            bunkerLossData={bunkerLossVM.bunkerLossData}
                            fuelTanks={getFuelTanks(vessel)}
                            selectedBunkerLoss={bunkerLossVM.selectedBunkerLoss}
                            deleteBunkerLoss={handleDeleteBunkerLoss}
                            selectBunkerLoss={handleSelectBunkerLoss}
                        />
                    )}

                    <BunkerLossEdit
                        column={column}
                        availableBunkerings={availableBunkerings}
                        allTanks={getFuelTanks(vessel)}
                        tankOptions={tankOptions}
                        selectedBunkerLoss={bunkerLossVM.selectedBunkerLoss}
                        handleSelectBunkering={handleSelectBunkering}
                        handleSelectTank={handleSelectTank}
                        handleChangeLoss={handleChangeLoss}
                        handleChangeLossReason={handleChangeLossReason}
                        handleChangeComment={handleChangeComment}
                        validComment={validComment}
                    />
                </div>
            }
        </OdfjellPanel>
    );
};

export default BunkerLossContainer;
